import { useState } from 'react'
import { requestBill, sendGuestNotification, type GuestNotificationTopic } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'

const COOLDOWN_MS = 60_000

/** Temas de notificación OhSir. Basado en el "llamar al mesero" categorizado y
 *  el enum TableNotificationType del OhSir original (llamar mesero / pedir la
 *  cuenta / pedir algo / reportar un problema). `freeText` habilita una nota. */
interface TopicDef {
  topic: GuestNotificationTopic
  icon: string
  label: string
  hint: string
  freeText?: boolean
  placeholder?: string
}
const TOPICS: readonly TopicDef[] = [
  { topic: 'CALL_WAITER', icon: '🙋', label: 'Llamar al mesero', hint: 'Alguien irá a tu mesa' },
  {
    topic: 'REQUEST_ITEM',
    icon: '🍴',
    label: 'Pedir algo más',
    hint: 'Cubiertos, servilletas, salsas, agua…',
    freeText: true,
    placeholder: '¿Qué necesitas? (opcional)',
  },
  {
    topic: 'REPORT_ISSUE',
    icon: '⚠️',
    label: 'Reportar un problema',
    hint: 'Cuéntale al staff qué pasó',
    freeText: true,
    placeholder: 'Describe el problema (opcional)',
  },
  {
    topic: 'OTHER',
    icon: '💬',
    label: 'Otro',
    hint: 'Cualquier otra cosa',
    freeText: true,
    placeholder: 'Describe lo que necesitas (opcional)',
  },
]

type SendState = 'idle' | 'sending' | 'sent' | 'error'

/** Floating actions: notificación categorizada al POS + pedir cuenta.
 *  Cooldown 60s por acción.
 *  `aboveCartBar`: cuando hay una barra de carrito fija abajo, eleva los FABs
 *  para que no tapen el botón "Revisar pedido". */
export function GuestActions(
  { aboveCartBar = false, showBill = true }: { aboveCartBar?: boolean; showBill?: boolean },
) {
  const session = useSessionStore((s) => s.session)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [selected, setSelected] = useState<TopicDef | null>(null)
  const [note, setNote] = useState('')
  const [notifyState, setNotifyState] = useState<SendState>('idle')
  const [billState, setBillState] = useState<SendState>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  if (!session) return null

  function closeSheet(): void {
    setSheetOpen(false)
    setSelected(null)
    setNote('')
  }

  async function sendNotification(): Promise<void> {
    if (!session || !selected) return
    setNotifyState('sending')
    setErrorMsg(null)
    const r = await sendGuestNotification(
      session.token,
      session.tableSessionId,
      selected.topic,
      selected.freeText ? note : undefined,
    )
    if (r.ok) {
      setNotifyState('sent')
      closeSheet()
      setTimeout(() => setNotifyState('idle'), COOLDOWN_MS)
    } else {
      setNotifyState('error')
      setErrorMsg(r.error.message)
      setTimeout(() => setNotifyState('idle'), 4000)
    }
  }

  async function doBill(): Promise<void> {
    if (!session) return
    setBillState('sending')
    setErrorMsg(null)
    const r = await requestBill(session.token, session.tableSessionId)
    if (r.ok) {
      setBillState('sent')
      setTimeout(() => setBillState('idle'), COOLDOWN_MS)
    } else {
      setBillState('error')
      setErrorMsg(r.error.message)
      setTimeout(() => setBillState('idle'), 4000)
    }
  }

  return (
    <>
      <div className={`guest-actions${aboveCartBar ? ' above-cart-bar' : ''}`}>
        {errorMsg && (
          <div className="toast danger-toast">{errorMsg}</div>
        )}
        {notifyState === 'sent' && <div className="toast">Notificación enviada al staff</div>}
        {billState === 'sent' && <div className="toast">Cuenta solicitada</div>}
        <button
          className={`fab ${notifyState === 'sent' ? '' : 'accent'}`}
          disabled={notifyState === 'sending'}
          onClick={() => setSheetOpen(true)}
        >
          {notifyState === 'sending' ? <span className="spinner" /> : null}
          <span>{notifyState === 'sent' ? '✓ Staff notificado' : 'Avisar al staff'}</span>
        </button>
        {/* "Pedir cuenta" sólo aplica a cuenta de MESA sin pagar — no en takeout ni
            en una orden ya pagada (el caller pasa showBill=false en esos casos). */}
        {showBill && (
          <button
            className="fab"
            disabled={billState !== 'idle' && billState !== 'error'}
            onClick={doBill}
          >
            {billState === 'sending' ? <span className="spinner" /> : null}
            <span>
              {billState === 'sending'
                ? 'Pidiendo cuenta…'
                : billState === 'sent'
                  ? '✓ Cuenta solicitada'
                  : 'Pedir cuenta'}
            </span>
          </button>
        )}
      </div>

      {sheetOpen && (
        <div className="notif-sheet-bg" onClick={closeSheet}>
          <div
            className="notif-sheet"
            role="dialog"
            aria-label="Avisar al staff"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="notif-sheet__handle" />
            <h3 className="notif-sheet__title">¿Qué necesitas?</h3>
            <p className="notif-sheet__sub">Elige un tema y avisamos al staff de tu mesa.</p>
            <div className="notif-sheet__grid">
              {TOPICS.map((tDef) => (
                <button
                  key={tDef.topic}
                  type="button"
                  className={`notif-topic${selected?.topic === tDef.topic ? ' is-selected' : ''}`}
                  onClick={() => {
                    setSelected(tDef)
                    if (!tDef.freeText) setNote('')
                  }}
                >
                  <span className="notif-topic__icon">{tDef.icon}</span>
                  <span className="notif-topic__label">{tDef.label}</span>
                  <span className="notif-topic__hint">{tDef.hint}</span>
                </button>
              ))}
            </div>
            {selected?.freeText && (
              <textarea
                className="notif-sheet__note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={selected.placeholder ?? 'Detalle (opcional)'}
                maxLength={280}
                rows={2}
              />
            )}
            <div className="notif-sheet__actions">
              <button type="button" className="notif-sheet__btn ghost" onClick={closeSheet}>
                Cancelar
              </button>
              <button
                type="button"
                className="notif-sheet__btn primary"
                disabled={!selected || notifyState === 'sending'}
                onClick={sendNotification}
              >
                {notifyState === 'sending' ? 'Enviando…' : 'Enviar aviso'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
